import api from './api';
import { User } from '../types';

export const usersService = {
  // Get all users (ADMIN / CO_ADMIN only)
  async getAll(): Promise<User[]> {
    const response = await api.get('/users');
    return response.data;
  },

  // Get leaderboard — limited public fields, accessible to all authenticated users
  async getLeaderboard(): Promise<Pick<User, 'id' | 'fullName' | 'profilePicture' | 'department' | 'branch'>[]> {
    const response = await api.get('/users/leaderboard');
    return response.data;
  },

  /** Branch names from user records (polls, forms). */
  async getBranches(): Promise<string[]> {
    const response = await api.get<string[]>('/users/branches');
    return response.data;
  },

  /** Department names; pass branch to limit to that branch. */
  async getDepartments(branch?: string): Promise<string[]> {
    const response = await api.get<string[]>('/users/departments', {
      params: branch?.trim() ? { branch: branch.trim() } : undefined,
    });
    return response.data;
  },

  // Get single user
  async getById(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Create new user
  async create(data: Partial<User> & { password: string }): Promise<User> {
    const response = await api.post('/users', data);
    return response.data;
  },

  // Update user (profile fields only — not `role`; self-update for any user, others require ADMIN/CO_ADMIN)
  async update(
    id: string,
    data: Omit<Partial<User>, 'role' | 'id' | 'createdAt' | 'updatedAt' | 'profilePicture'> & {
      password?: string;
    },
  ): Promise<User> {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },

  /** ADMIN only — PATCH /users/:id/role */
  async changeRole(id: string, role: User['role']): Promise<User> {
    const response = await api.patch(`/users/${id}/role`, { role });
    return response.data;
  },

  // Delete user
  async delete(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  // Upload profile picture
  async uploadProfilePicture(file: File): Promise<User> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/users/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Download the CSV import template (headers + one example row).
   *
   * SECURITY: this never includes real user data. The earlier implementation
   * had a fallback that called `getAll()` and dumped every user (PII) into the
   * template if the server returned 404. That fallback has been removed.
   */
  async downloadImportTemplate(): Promise<void> {
    const STATIC_TEMPLATE =
      'Emp No,Full Name,Department,Branch,Password,Role,User Email,Designation,Mobile\n' +
      'E001,John Doe,IT,Kandy,TempPass123!,EMPLOYEE,john.doe@company.com,Engineer,0741234567';

    const triggerDownload = (csv: BlobPart, filename: string) => {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    };

    try {
      const response = await api.get('/users/import-template', {
        responseType: 'blob',
      });
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'user-import-template.csv';
      triggerDownload(response.data, filename);
    } catch (error: any) {
      // If the server endpoint is unavailable, generate the same blank template
      // locally — still no user data involved.
      if (error?.response?.status === 404) {
        triggerDownload(STATIC_TEMPLATE, 'user-import-template.csv');
        return;
      }
      throw error;
    }
  },
};
